/**
 * Route → screen. SPEC.md §47, §35.
 *
 * Three screens and a dead end. History and settings are drawers on the table
 * screen, so they are not routes. §47
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { RouteNotFound, navigate, paths, useRoute } from '@/router';
import { GamePage } from '@/features/game/GamePage';
import { UndoToast } from '@/features/scoring/UndoToast';
import { JoinSessionPage } from '@/features/session/JoinSessionPage';
import { LandingPage } from '@/features/session/LandingPage';
import { TutorialRoute } from '@/features/tutorial/TutorialRoute';

interface BoundaryState {
  failed: boolean;
}

/**
 * Last line of defence. A render crash shows friendly copy and a way out — never
 * a stack trace, which is what the console is for. §35
 */
class ErrorBoundary extends Component<{ children: ReactNode }, BoundaryState> {
  override state: BoundaryState = { failed: false };

  static getDerivedStateFromError(): BoundaryState {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled UI error', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      // Centred when it fits, scrolled when it does not — `justify-center` alone
      // would push the heading above the top of a short viewport, taking the way
      // out with it. Same `m-auto` as the landing screen, for the same reason. §18.2
      <main className="scroll-area flex min-h-0 flex-1 flex-col p-safe">
        <div className="m-auto flex flex-col items-center gap-5 text-center">
          <h1 className="text-2xl font-semibold text-ink">Something went wrong</h1>
          <p className="max-w-xs text-ink-muted">
            Your table is safe — the scores live on the server. Reload to pick up where you left
            off.
          </p>
          <button
          type="button"
            onClick={() => {
              window.location.reload();
            }}
            className="min-h-tap rounded-control bg-accent px-6 py-3 font-semibold text-accent-ink"
          >
            Reload
          </button>
          <button
            type="button"
            onClick={() => {
              this.setState({ failed: false });
              navigate(paths.landing(), { replace: true });
            }}
            className="min-h-tap rounded-control px-6 py-3 font-semibold text-ink-muted"
          >
            Back to start
          </button>
        </div>
      </main>
    );
  }
}

function CurrentScreen() {
  const route = useRoute();

  switch (route.name) {
    case 'landing':
      return <LandingPage />;
    case 'join':
      return <JoinSessionPage />;
    case 'game':
      return <GamePage />;
    case 'tutorial':
      // Behind a dynamic import — see TutorialRoute for why it is loaded this
      // way and what happens when the chunk cannot be fetched. §17.1, §44
      return <TutorialRoute locale={route.locale} />;
    case 'not-found':
      return <RouteNotFound />;
  }
}

export function App() {
  return (
    <ErrorBoundary>
      <CurrentScreen />
      {/*
        One toast host for the whole app. A player bounced off a table arrives on
        the landing screen with the reason still queued, so the host cannot live
        inside the table screen. It lifts itself clear of the keypad using
        the height GamePage publishes. §12.1, §35
      */}
      <UndoToast />
    </ErrorBoundary>
  );
}
